import {configuration} from './Configuration'
import {Alarm, AlarmGroup, DownTime} from '../model/DownTime'
import {DowntimeEditStatus} from '../model/DowntimeEditStatus'
import {APIError, ValidationQueryResult, ValidationResults} from '../model/ValidationResults'
import axios, {AxiosPromise, AxiosRequestConfig} from 'axios'
import AvailabilityDetails from '../model/AvailabilityDetails'
import {addAuthHeader} from './SiteConfig-api'

// Currently the API expects to recieve downtimes with no timezone information. Implicitly, we know that all times are in the site's
// timezone but as we don't have this information available in the UI we work with timestamps as though they are UTC and just remove
// the trailing Z before posting to the API. This leaves it up to the user to specify times in whatever timezone they like (assumed
// to be the site's timezone) and the datetime string they enter will go directly into the database.
// eslint-disable-next-line
Date.prototype.toJSON = function(): string {
  if(!isNaN(this.getTime())){
    var correctedDate = new Date(this)
    // remove the trailing Z because the date is not in UTC
    return correctedDate.toISOString().replace('Z', '')
  } else {
    return "";
  }
}

export const recalculate = (marketKey: string, apikey: string, month: Date): Promise<string> =>
  new Promise(async function(resolve, reject) {
    axios
      .put<string>(getRecalculateURL(marketKey, apikey, month.toJSON()), null,
        { withCredentials: true, headers: await addAuthHeader('SmartApi', marketKey) })
      .then(function(response) {
        resolve(response.data)
        console.log(response)
      })
      .catch(function(error) {
        reject(error.response ? (error.response.data.message ? error.response.data.message : error.response.statusText) : error.message)
        console.log(error)
      })
  })

export const getRecalculateURL = (marketKey: string, apikey: string, month: string): string =>
  `${configuration.SmartAPI}availability/recalculate/${marketKey}/${apikey}/${month}/`

export const getGeneratorAvailability = (
  marketKey: string,
  apikey: string,
  month: Date
): Promise<AvailabilityDetails> =>
  getAvailability(
    `${
      configuration.SmartAPI
    }availability/month/generator/${marketKey}/${apikey}/${month.toJSON()}/`,
    marketKey
  )

export const getSiteAvailability = (
  marketKey: string,
  apikey: string,
  month: Date
): Promise<AvailabilityDetails> =>
  getAvailability(
    `${configuration.SmartAPI}availability/month/site/${marketKey}/${apikey}/${month.toJSON()}/`,
    marketKey
  )

const getAvailability = (url: string, marketKey: string): Promise<AvailabilityDetails> =>
  new Promise(async function(resolve, reject) {
    axios
      .get<AvailabilityDetails>(url, {
        withCredentials: true,
        headers: await addAuthHeader('SmartApi', marketKey)
      })
      .then(function(response) {
        resolve(response.data)
        console.log(response)
      })
      .catch(function(error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })

export const getDowntimeEditStatus = (
  marketKey: string,
  apikey: string,
  month: Date
): Promise<DowntimeEditStatus> =>
  new Promise(async function(resolve, reject) {
    axios
      .get<DowntimeEditStatus>(getDowntimeEditStatusURL(marketKey, apikey, month.toJSON()), {
        withCredentials: true,
        headers: await addAuthHeader('SmartApi', marketKey)
      })
      .then(function(response) {
        resolve(new DowntimeEditStatus(response.data))
        console.log(response)
      })
      .catch(function(error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })

export const getDowntimeEditStatusURL = (
  marketKey: string,
  apikey: string,
  month: string
): string => `${configuration.SmartAPI}alarm-edit-status/${apikey}?month=${month}`

export const updateDowntimeEditStatus = (
  marketKey: string,
  siteKey: string,
  month: Date,
  status: string
): Promise<void> => {
  return new Promise(async function(resolve, reject) {
    axios
      .post<void>(
        `${configuration.SmartAPI}alarm-edit-status`,
        { siteKey, month: month.toJSON(), status },
        { withCredentials: true, headers: await addAuthHeader('SmartApi', marketKey) }
      )
      .then(data => {
        resolve()
        console.log(data)
      })
      .catch(function(error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

// Currently the API returns list of downtimes with no timezone information. Implicitly, we know that all times are in the site's timezone
// but as we don't have this information available in the UI we want to work with timestamps as though they are just date and time strings
// and avoid timezone conversions. This leaves it up to the user to input whatever time they like, and that string will go directly into
// the database (assumed to be in the site's timezone). The easiest way to be timezone ambivalent is to pretend that everything is UTC.
function checkAndFixTimezone(date: Date, fieldName: string) {
  if (date.toString().includes("+") || date.toString().includes("Z")) {
    console.warn("A downtime was recieved from the api with a " + fieldName + " timezone offset, which might cause bugs in the data: " + date)
    throw new Error("Downtime with timezone information is invalid")
  }

  return new Date(date + "Z")
}

export const getDowntimes = (
  marketKey: string,
  apikey: string,
  from: Date,
  to: Date,
  scadaObjectKey: string,
  scadaObjectType: string
): Promise<DownTime[]> => {
  return new Promise(async function(resolve, reject) {
    axios
      .get<DownTime[]>(
        getDowntimesURL(
          marketKey,
          apikey,
          from.toJSON(),
          to.toJSON(),
          scadaObjectKey,
          scadaObjectType
        ),
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) }
      )
      .then(function(response) {
        resolve(response.data.map(item => {
          item.timeOn = checkAndFixTimezone(item.timeOn, "timeOn")
          item.timeOff = item.timeOff && checkAndFixTimezone(item.timeOff, "timeOff")
          return new DownTime(item)
        }))
        console.log(response)
      })
      .catch(function(error) {
        reject(error.response ? error.response.statusText : error.message)
        console.error(error)
      })
  })
}

export function getDowntimesURL(
  marketKey: string,
  apikey: string,
  from: string,
  to: string,
  scadaObjectKey: string,
  scadaObjectType: string
): string {
  const apiRoot = configuration.DowntimeAPI(marketKey)
  if (scadaObjectType === '' && scadaObjectKey === 'All') {
    return `${apiRoot}downtime/site/${apikey}?from=${from}&to=${to}`
  } else if (scadaObjectType !== '' && scadaObjectKey === 'All') {
    return `${apiRoot}downtime/${apikey}/type/${scadaObjectType}?from=${from}&to=${to}`
  } else {
    return `${apiRoot}downtime/object/${scadaObjectKey}?from=${from}&to=${to}`
  }
}

export const getPenalisingStatus = (marketKey: string): Promise<string[]> => {
  return new Promise(async function (resolve, reject) {
    axios
      .get<{ name: string }[]>(
        `${configuration.DowntimeAPI(marketKey)}penalising-status`,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        resolve(response.data.map(item => item.name))
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

export const getAlarmGroups = (marketKey: string): Promise<AlarmGroup[]> => {
  return new Promise(async function (resolve, reject) {
    axios
      .get(`${configuration.DowntimeAPI(marketKey)}group`,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        resolve(
          response.data.map((item: any) => ({
            ...item,
            technology: item.technology.name,
            subGroups: item.subGroups.map((subItem: { name: string }) => subItem.name)
          }))
        )
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

export const getAlarmsForDowntime = (
  marketKey: string,
  scadaObjectKey: string,
  timeOn: Date,
  timeOff: Date
): Promise<Alarm[]> => {
  return new Promise(async function (resolve, reject) {
    axios
      .get<Alarm[]>(
        `${configuration.DowntimeAPI(marketKey)}alarm/${scadaObjectKey}
        ?time-on=${timeOn.toJSON()}&time-off=${timeOff.toJSON()}`,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) }
      )
      .then(function (response) {
        resolve(response.data.map(d => {
            d.timeOn = checkAndFixTimezone(d.timeOn, "timeOn")
            d.timeOff = d.timeOff && checkAndFixTimezone(d.timeOff, "timeOff")
            return new Alarm(d)
          }
        ))
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

export const checkDowntimeValidation = (
  marketKey: string,
  downtime: DownTime
): Promise<ValidationResults> => {
  return new Promise(async function (resolve, reject) {
    axios
      .get<ValidationResults>(
        getValidationURL(marketKey, downtime.scadaObjectKey, downtime.timeOn, downtime.timeOff),
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(data => {
        resolve(data.data)
        console.log(data)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
      })
  })
}

export function getValidationURL(
  marketKey: string,
  scadaObjectKey: string,
  timeOn: Date,
  timeOff?: Date
): string {
  let timeParams = `?time-on=${timeOn.toJSON()}`

  if (timeOff) {
    timeParams = timeParams.concat(`&time-off=${timeOff.toJSON()}`);
  }

  return `${configuration.DowntimeAPI(
    marketKey
  )}downtime/validate/${scadaObjectKey}${timeParams}`
}

export const updateDowntime = (
  marketKey: string,
  downtime: DownTime
): Promise<ValidationResults | undefined> => sendDowntimeToAPI(marketKey, downtime, axios.put)

export const createDowntime = (
  marketKey: string,
  downtime: DownTime
): Promise<ValidationResults | undefined> => sendDowntimeToAPI(marketKey, downtime, axios.post)

const sendDowntimeToAPI = (
  marketKey: string,
  downtime: DownTime,
  method: (url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise<null>
): Promise<ValidationResults | undefined> => {
  return new Promise(async function (resolve, reject) {
    method(`${configuration.DowntimeAPI(marketKey)}downtime`,
      downtime,
      { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(data => {
        resolve(undefined)
        console.log(data)
      })
      .catch((error: APIError<ValidationQueryResult>) => {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message === 'Validation Errors'
        ) {
          resolve(error.response.data.details)
        } else {
          reject(error.response ? error.response.statusText : error.message)
          console.log(error)
        }
      })
  })
}

export const revertDowntime = (marketKey: string, downtime: DownTime): Promise<DownTime> => {
  return new Promise(async function (resolve, reject) {
    axios
      .put<DownTime>(
        `${configuration.DowntimeAPI(marketKey)}downtime/revert/${
          downtime.scadaObjectKey
        }?time-on=${downtime.timeOn.toJSON()}`,
        null,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        response.data.timeOn = checkAndFixTimezone(response.data.timeOn, "timeOn")
        response.data.timeOff = response.data.timeOff && checkAndFixTimezone(response.data.timeOff, "timeOff")
        resolve(
          new DownTime(response.data))
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

export const splitDowntime = (
  marketKey: string,
  downtime: DownTime,
  splitTime: Date
): Promise<DownTime[]> => {
  return new Promise(async function (resolve, reject) {
    axios
      .put<DownTime[]>(
        `${configuration.DowntimeAPI(marketKey)}downtime/split/${
          downtime.scadaObjectKey
        }?time-on=${downtime.timeOn.toJSON()}&split-time=${splitTime.toJSON()}`,
        null,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        resolve(response.data.map(d => {
          d.timeOn = checkAndFixTimezone(d.timeOn, "timeOn")
          d.timeOff = d.timeOff && checkAndFixTimezone(d.timeOff, "timeOff")
          return new DownTime(d)
        }))
        console.log(response)
      })
      .catch((error: APIError<ValidationQueryResult>) => {
        if (
          error.response &&
          error.response.data &&
          error.response.data.message === 'Validation Errors' &&
          error.response.data.details &&
          error.response.data.details.errors
        ) {
          reject(error.response.data.details.errors.map(e => e.message).join(', '))
        } else {
          reject(error.response ? error.response.statusText : error.message)
          console.log(error)
        }
      })
  })
}

export const copyDowntime = (
  marketKey: string,
  source: DownTime,
  targets: DownTime[]
): Promise<void> => {
  return new Promise(async function (resolve, reject) {
    axios
      .put<DownTime[]>(
        `${configuration.DowntimeAPI(marketKey)}downtime/copy/${
          source.scadaObjectKey
        }?time-on=${source.timeOn.toJSON()}`,
        targets.map(event => ({scadaObjectKey: event.scadaObjectKey, timeOn: event.timeOn})),
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        resolve()
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}

export const canEditTimeOff = (
  marketKey: string,
  scadaObjectKey: string,
  timeOn: Date
): Promise<boolean> => {
  return new Promise(async function (resolve, reject) {
    axios
      .get<boolean>(
        `${configuration.DowntimeAPI(
          marketKey
        )}downtime/timeoff/editable/${scadaObjectKey}?time-on=${timeOn.toJSON()}`,
        { withCredentials: true, headers: await addAuthHeader('DowntimeApi', marketKey) })
      .then(function (response) {
        resolve(response.data)
        console.log(response)
      })
      .catch(function (error) {
        reject(error.response ? error.response.statusText : error.message)
        console.log(error)
      })
  })
}
